import { Component, OnInit, OnDestroy, EventEmitter, Output } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { FormBuilder, FormGroup, FormArray, AbstractControl } from '@angular/forms';
import { Job } from 'app/models';
import { Subscription } from 'rxjs';
import { OrderService } from 'app/services/order.service';
import { AuthenticationService } from 'app/services/authentication.service';
import { LAMINATING } from '../../../../ngrx/status-types';
import { ScannerService } from 'app/services/scanner.service';

@Component({
  selector: 'app-rfid-laminate-modal',
  templateUrl: './rfid-laminate-modal.component.html',
  styleUrls: ['./rfid-laminate-modal.component.scss'],
})
export class RfidLaminateModalComponent implements OnInit, OnDestroy {
          laminateForm      : FormGroup;
          jobs              : any[] = [];
          selectAllChecked  : boolean = false;
          selectedCount     : number = 0;
          totalSheets       : number = 0;
          totalSheetsAllJobs: number = 0;
          daylightLimit     : number = 120;
          isAdmin           : boolean = false;
          isSupervisor      : boolean = false;
  private formSubscription  : Subscription;
  private initSubscriptions: Subscription[] = [];

  @Output() emitJobs = new EventEmitter<any>();

  constructor(
    public bsModalRef: BsModalRef,
    private fb: FormBuilder,
    private orderService: OrderService,
    private scanner: ScannerService,
    private authService: AuthenticationService
  ) {
    this.createForm();
    this.isAdmin = this.authService.userIsAdmin();
    this.isSupervisor = this.authService.userIsSupervisor();
  }

  ngOnInit(): void {
    this.initializeForm();
    this.scanner.setExclusiveMode(true);
    console.log('RFID Laminate modal: Exclusive mode enabled');
    this.initSubscriptions = [
      this.formSubscription = this.getJobsFormArray().valueChanges
        .subscribe(() => {
          this.updateSelectionState();
        }),
      
      this.scanner._laminationScanListener
        .subscribe(id => {
          const ID = id.replace(/[^A-Za-z0-9-]/g, '');
          const [l, t] = this.scanner._triggers;
          const location = this.scanner._locations[l];
          console.log(`RFID Laminate ID ${ID} scanned at ${location}`);

          // Split the scanned ID into orderId and jobId
          const [orderId, jobId] = ID.split('-');
          console.log(`Parsed OrderID: ${orderId}, JobID: ${jobId}`);

          // find/toggle the matching job
          const jobsFormArray = this.getJobsFormArray();
          const jobIndex = jobsFormArray.controls.findIndex(control => {
            const controlJobId = control.get('id').value;
            const controlOrderId = control.get('orderId').value;
            return (controlJobId === jobId && controlOrderId === orderId);
          });

          if (jobIndex !== -1) {
            const jobFormGroup = jobsFormArray.at(jobIndex);
            const currentValue = jobFormGroup.get('selected').value;
            
            jobFormGroup.patchValue({
              selected: !currentValue
            });
            
            // console.log(`Toggling job at index ${jobIndex} from ${currentValue} to ${!currentValue}`);
          } else {
            console.log(`No matching job found for OrderID: ${orderId}, JobID: ${jobId}`);
          }
        })
    ];
  }

  ngOnDestroy(): void {
    // Disable exclusive mode when the component is destroyed
    this.scanner.setExclusiveMode(false);
    console.log('RFID Laminate modal: Exclusive mode disabled');
    
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }

    this.initSubscriptions.forEach(subscription => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });
  }

  initializeForm(): void {
    const formArray = this.jobs.map(job => 
      this.fb.group({
        selected: [false],
        id      : [job.id],
        orderId : [job.orderId],
        sheets  : [job.sheets],
        dueDate : [job.dueDate],
        finishType : [job.rfid_finishType]
      })
    );
    
    this.laminateForm.setControl('jobs', this.fb.array(formArray));
  }

  private createForm(): void {
    this.laminateForm = this.fb.group({
      jobs: this.fb.array([])
    });
  }

  private getJobsFormArray(): FormArray {
    return this.laminateForm.get('jobs') as FormArray;
  }

  private updateSelectionState(): void {
    const jobs = this.getJobsFormArray();
    const selectedJobs = jobs.controls.filter(control => control.get('selected').value);
    
    this.selectedCount = selectedJobs.length;
    this.selectAllChecked = jobs.length > 0 && selectedJobs.length === jobs.length;
    this.totalSheets = selectedJobs.reduce((sum, control) => sum + (control.get('sheets').value || 0), 0);
    this.totalSheetsAllJobs = jobs.controls.reduce((sum, control) => sum + (control.get('sheets').value || 0), 0);
  }

  toggleSelectAll(select: boolean): void {
    if (!this.isAdmin && !this.isSupervisor) return;
    
    const jobs = this.getJobsFormArray();
    jobs.controls.forEach(control => {
      control.get('selected').setValue(select);
    });
  }

  // This won't call the QA Check modal from COLLATED -> LAMATING
  beginLaminating(): void {
    const selectedJobs = this.getSelectedJobs();
    
    // Disable exclusive mode before hiding the modal
    this.scanner.setExclusiveMode(false);
    console.log('RFID Laminate modal: Exclusive mode disabled (begin laminating)');
    
    // Update jobs to LAMINATING status
    selectedJobs.forEach(job => {
      this.orderService.setJobStatus(
        job,
        LAMINATING,
        null, // reason translation
        undefined, // additional params
        null // other reason
      );
    });

    this.bsModalRef.hide();
  }

  // This will call the QA check modal from LAMINATING -> LAMINATED, 
  // hence we just emit the jobs we will update in QA
  finishLaminating(): void {
    const selectedJobs = this.getSelectedJobs();
    
    // Disable exclusive mode before hiding the modal
    this.scanner.setExclusiveMode(false);
    console.log('RFID Laminate modal: Exclusive mode disabled (finish laminating)');
    
    this.bsModalRef.hide();
    
    this.emitJobs.emit({
      jobs: selectedJobs,
      status: 'laminatingCheck'
    });
  }

  private getSelectedJobs(): Job[] {
    const selectedJobIds = this.getJobsFormArray().controls
      .filter(control => control.get('selected').value)
      .map(control => control.get('id').value);

    return this.jobs.filter(job => selectedJobIds.indexOf(job.id) !== -1);
  }

  handleCheckboxClick(event: MouseEvent, control: AbstractControl): void {
    // user is admin, allow normal checkbox behavior
    if (this.isAdmin || this.isSupervisor) {
      return;
    }

    // non-admin users:
    // checkbox is currently checked, allow unchecking
    // checkbox is unchecked, prevent checking
    if (!control.get('selected').value) {
      event.preventDefault();
      return;
    }
  }
} 


// WEBPACK FOOTER //
// /Users/itu/Desktop/impreso_updated_sheets/impresocloud/www/client/src/app/shared/modals/rfid-laminate-modal/rfid-laminate-modal.component.ts